import * as React from "react";
import { WizardCruisePWAProps } from "./typings";
import { CruiseDestination } from "components/flexComponents/WizardCruisePWA/components/CruiseDestination";
import { observer } from "mobx-react";
import { LobTitle } from "src/components/shared/LobTitle/LobTitle";
import { SubmitRow } from "src/components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { Dates } from "src/components/shared/Dates/Dates";
import { Travelers } from "src/components/shared/Travelers/Travelers";
import { LobSubtitle } from "src/components/shared/LobSubtitle/LobSubtitle";
import { NoRehydratationValues, TravelersTriggerTypes } from "src/components/shared/TravelersField/typings";
import { Viewport, ViewSmall, ViewMedium } from "@shared-ui/viewport-context";
import {
  UitkLayoutGrid,
  UitkLayoutConditionalGridSpan,
  UitkLayoutConditionalGridColumnRow,
} from "uitk-react-layout-grid";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { isVariantEnabled, Experiment } from "src/components/utility/experiment";

export const WizardCruisePWAView: React.FunctionComponent<WizardCruisePWAProps> = observer(
  ({ flexComponent, wizardState, context }) => {
    const [startId, setStartId] = React.useState<string | undefined>("earliest-departure-date");
    const [endId, setEndId] = React.useState<string | undefined>("latest-departure-date");

    /**
     * This hook is reached for rehydratation, so if JS is enabled there is no need of keep setting IDs to elements
     * so value is changed to 'undefined' and so it is avoided, if JS is disabled then this hook is never executed
     */
    React.useEffect(() => {
      setStartId(undefined);
      setEndId(undefined);
    }, []);
    const {
      cruiseWizardState,
      cruiseWizardState: { config: cruiseConfig },
    } = wizardState;

    const displayTravelersInline = isVariantEnabled(context, Experiment.Alignment_of_Traveler_Input);

    const mainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 1, medium: 6 };
    const destinationColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: displayTravelersInline ? 2 : 3 };
    const travelersColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 3 };
    const datesColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: displayTravelersInline ? 4 : 6 };

    /**
     * Values used if no rehydratation(JS disabled)
     */
    const noRehydratationValues: NoRehydratationValues = {
      numOfTravelers: 2,
      name: "adultCount",
    };

    const getLayoutTravelersInline = (alignment: "end" | "start") => (
      <UitkLayoutFlex
        data-testid="travelers-inline-link"
        alignItems={alignment}
        justifyContent={alignment}
        space="six"
        className="all-l-padding-six"
      >
        <>
          <UitkLayoutFlexItem alignSelf={alignment}>
            <Travelers
              lobState={cruiseWizardState}
              colSpan={travelersColSpan}
              noRehydratationValues={noRehydratationValues}
              triggerType={TravelersTriggerTypes.LINK_BUTTON}
            />
          </UitkLayoutFlexItem>
        </>
      </UitkLayoutFlex>
    );

    return (
      <form
        noValidate={true}
        action={cruiseConfig.form.action}
        autoComplete="off"
        className="WizardCruisePWA"
        id={cruiseConfig.elementId}
        data-fm={flexComponent.fmId}
        data-fm-title-id={flexComponent.fmTitleId}
        data-testid="wizard"
        onSubmit={cruiseWizardState.submit}
      >
        <LobTitle lobState={cruiseWizardState} />
        {cruiseWizardState.config.heading.visibleLobSubtitle && (
          <LobSubtitle lobState={cruiseWizardState} className={"all-b-padding-four all-t-margin-one"} />
        )}

        {displayTravelersInline && (
          <Viewport>
            <ViewSmall>{getLayoutTravelersInline("start")}</ViewSmall>
            <ViewMedium>{getLayoutTravelersInline("end")}</ViewMedium>
          </Viewport>
        )}
        <UitkLayoutGrid columns={mainGridColumns} space="three" className="all-t-margin-three">
          <>
            <CruiseDestination lobState={cruiseWizardState} colSpan={destinationColSpan} />
            {!displayTravelersInline && (
              <Travelers
                lobState={cruiseWizardState}
                colSpan={travelersColSpan}
                noRehydratationValues={noRehydratationValues}
              />
            )}
            <Viewport>
              <ViewSmall>
                <Dates
                  startId={startId}
                  endId={endId}
                  lobState={cruiseWizardState}
                  colSpan={datesColSpan}
                  stackedDates={true}
                />
              </ViewSmall>
              <ViewMedium>
                <Dates startId={startId} endId={endId} lobState={cruiseWizardState} colSpan={datesColSpan} />
              </ViewMedium>
            </Viewport>
          </>
        </UitkLayoutGrid>

        <SubmitRow lobState={cruiseWizardState} rfrr={"SearchClick_Cruises"} />
      </form>
    );
  }
);

export default WizardCruisePWAView;
